import {
  ref,
  get,
  set,
  update,
  remove,
  onValue,
  query,
  orderByChild,
  equalTo,
  push,
  off,
} from 'firebase/database';
import { rtdb } from '../../config/firebase';
import type { AccessRequest } from '../../types';
import { dateToMs, msToDate, snapshotToArray, stripUndefined } from './helpers';

const PATH = 'accessRequests';

type Unsubscribe = () => void;

const fromRtdb = (id: string, data: any): AccessRequest => ({
  id,
  email: data.email,
  message: data.message,
  status: data.status,
  approvedRole: data.approvedRole,
  approvedHomeIds: data.approvedHomeIds,
  createdAt: msToDate(data.createdAt) as Date,
  approvedAt: msToDate(data.approvedAt),
});

export const createAccessRequest = async (
  email: string,
  message?: string
): Promise<void> => {
  const newRef = push(ref(rtdb, PATH));
  await set(
    newRef,
    stripUndefined({
      email: email.toLowerCase(),
      message: message || '',
      status: 'pending',
      createdAt: Date.now(),
    })
  );
};

export const getAllAccessRequests = async (): Promise<AccessRequest[]> => {
  const snapshot = await get(ref(rtdb, PATH));
  return snapshotToArray<AccessRequest>(snapshot.val(), fromRtdb);
};

/**
 * Get approved access request by email.
 * Uses orderByChild on email, then filters status in-memory.
 */
export const getApprovedRequestByEmail = async (
  email: string
): Promise<AccessRequest | null> => {
  const q = query(
    ref(rtdb, PATH),
    orderByChild('email'),
    equalTo(email.toLowerCase())
  );
  const snapshot = await get(q);
  const requests = snapshotToArray<AccessRequest>(snapshot.val(), fromRtdb);
  return requests.find((r) => r.status === 'approved') ?? null;
};

export const updateAccessRequest = async (
  id: string,
  updates: Partial<Pick<AccessRequest, 'status' | 'approvedRole' | 'approvedHomeIds' | 'approvedAt'>>
): Promise<void> => {
  const data: any = { ...updates };
  if (updates.approvedAt !== undefined) {
    data.approvedAt = dateToMs(updates.approvedAt);
  }
  await update(ref(rtdb, `${PATH}/${id}`), stripUndefined(data));
};

export const deleteAccessRequest = async (id: string): Promise<void> => {
  await remove(ref(rtdb, `${PATH}/${id}`));
};

export const subscribeToAccessRequests = (
  callback: (requests: AccessRequest[]) => void,
  onError?: (error: Error) => void
): Unsubscribe => {
  const r = ref(rtdb, PATH);
  const handler = onValue(
    r,
    (snapshot) => {
      callback(snapshotToArray<AccessRequest>(snapshot.val(), fromRtdb));
    },
    onError
  );
  return () => off(r, 'value', handler);
};
